import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as eventService from "../services/eventService";
import * as invitationLogService from "../services/invitationLogService";

function claseEstado(estado: string) {
  if (estado === "ENVIADO") return "bg-emerald-500/10 text-emerald-500 border border-emerald-500/20";
  if (estado === "ACEPTADO") return "bg-sky-500/10 text-sky-500 border border-sky-500/20";
  if (estado === "FALLIDO") return "bg-red-500/10 text-red-500 border border-red-500/20";
  return "bg-amber-500/10 text-amber-500 border border-amber-500/20";
}

function formatoFechaHora(iso: string) {
  try {
    return new Intl.DateTimeFormat("es-PE", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function porcentaje(valor: number, total: number) {
  if (!total) return 0;
  return Math.round((valor / total) * 100);
}

function InvitationLogsPage() {
  const navigate = useNavigate();
  const { eventoId } = useParams();
  const idEvento = Number(eventoId);

  const [eventoNombre, setEventoNombre] = useState("Evento");
  const [eventoFecha, setEventoFecha] = useState<string | null>(null);
  const [logs, setLogs] = useState<invitationLogService.InvitationLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [estado, setEstado] = useState("TODOS");
  const [detailLog, setDetailLog] = useState<invitationLogService.InvitationLog | null>(null);

  async function cargar() {
    if (!Number.isFinite(idEvento)) return;
    setLoading(true);
    setErrorMsg(null);

    try {
      try {
        const ev = await eventService.obtenerEvento(idEvento);
        setEventoNombre(ev.nombre);
        setEventoFecha(ev.fechaPrincipal);
      } catch {
        // La vista puede funcionar solo con los logs.
      }

      const data = await invitationLogService.listarLogsInvitacionesPorEvento(idEvento);
      setLogs(data ?? []);
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : "Error cargando logs de invitaciones");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idEvento]);

  const metricas = useMemo(() => {
    const total = logs.length;
    const enviados = logs.filter((log) => log.estado === "ENVIADO").length;
    const aceptados = logs.filter((log) => log.estado === "ACEPTADO").length;
    const fallidos = logs.filter((log) => log.estado === "FALLIDO").length;
    const pendientes = Math.max(total - enviados - aceptados - fallidos, 0);
    const canales = new Set(logs.map((log) => log.canal).filter(Boolean)).size;
    const ultimo = [...logs].sort((a, b) => new Date(b.enviadoEn).getTime() - new Date(a.enviadoEn).getTime())[0];

    return {
      total,
      enviados,
      aceptados,
      fallidos,
      pendientes,
      canales,
      tasaAceptacion: porcentaje(aceptados, total),
      tasaSinFallo: porcentaje(enviados + aceptados, total),
      ultimo,
    };
  }, [logs]);

  const estados = useMemo(() => {
    return Array.from(new Set(logs.map((log) => log.estado).filter(Boolean))).sort();
  }, [logs]);

  const filtrados = useMemo(() => {
    const query = q.trim().toLowerCase();
    return logs.filter((log) => {
      const nombre = `${log.invitado?.nombre ?? ""} ${log.invitado?.apellido ?? ""}`.trim().toLowerCase();
      const matchQuery =
        !query ||
        nombre.includes(query) ||
        (log.invitado?.telefono ?? "").toLowerCase().includes(query) ||
        log.canal.toLowerCase().includes(query) ||
        log.modo.toLowerCase().includes(query) ||
        (log.errorDetalle ?? "").toLowerCase().includes(query);
      const matchEstado = estado === "TODOS" ? true : log.estado === estado;
      return matchQuery && matchEstado;
    });
  }, [logs, q, estado]);

  return (
    <>
      <div className="flex-1 overflow-y-auto p-4 md:p-8 lg:px-12">
        <div className="max-w-7xl mx-auto flex flex-col gap-8">
        <div className="flex flex-col gap-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <button
                type="button"
                onClick={() => navigate("/home/rsvp")}
                className="text-[#637588] dark:text-[#ab9db8] font-medium hover:text-primary transition-colors flex items-center gap-1"
              >
                <span className="material-symbols-outlined text-base">arrow_back</span>
                RSVP
              </button>
              <span className="material-symbols-outlined text-[#637588] dark:text-[#ab9db8] text-base">
                chevron_right
              </span>
              <span className="text-[#111418] dark:text-white font-semibold">Invitaciones enviadas</span>
            </div>

            <button
              type="button"
              onClick={cargar}
              className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition-colors dark:bg-[#191121] dark:hover:bg-white/5"
            >
              <span className="material-symbols-outlined text-[18px]">refresh</span>
              Actualizar
            </button>
          </div>

          <div className="flex flex-col gap-2">
            <h1 className="text-[#111418] dark:text-white text-2xl sm:text-3xl md:text-4xl font-black leading-tight tracking-tight">
              Invitaciones enviadas:{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-purple-400">
                {eventoNombre}
              </span>
            </h1>
            <p className="text-[#637588] dark:text-[#ab9db8] text-base font-normal">
              Monitorea envíos, fallos y trazabilidad por invitado.
            </p>
          </div>
        </div>

        {loading && <div className="text-[#637588] dark:text-[#ab9db8]">Cargando logs de invitaciones...</div>}
        {!loading && errorMsg && <div className="text-red-400">{errorMsg}</div>}

        {!loading && !errorMsg && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <div className="flex flex-col gap-2 rounded-xl p-5 border border-[#e0e0e0] dark:border-[#473c53] bg-white dark:bg-[#231b2e] shadow-sm">
                <div className="flex justify-between items-start">
                  <p className="text-[#637588] dark:text-[#ab9db8] text-sm font-medium uppercase tracking-wider">
                    Total de envíos
                  </p>
                  <span className="material-symbols-outlined text-primary">outgoing_mail</span>
                </div>
                <p className="text-[#111418] dark:text-white text-3xl font-bold leading-tight">{metricas.total}</p>
                <div className="text-sm text-[#637588] dark:text-[#ab9db8]">
                  {metricas.canales} canales registrados
                </div>
              </div>

              <div className="flex flex-col gap-2 rounded-xl p-5 border border-[#e0e0e0] dark:border-[#473c53] bg-white dark:bg-[#231b2e] shadow-sm">
                <div className="flex justify-between items-start">
                  <p className="text-[#637588] dark:text-[#ab9db8] text-sm font-medium uppercase tracking-wider">
                    Aceptados
                  </p>
                  <span className="material-symbols-outlined text-sky-500">task_alt</span>
                </div>
                <p className="text-[#111418] dark:text-white text-3xl font-bold leading-tight">{metricas.aceptados}</p>
                <div className="text-sm text-[#637588] dark:text-[#ab9db8]">
                  {metricas.tasaAceptacion}% aceptado por el servicio
                </div>
              </div>

              <div className="flex flex-col gap-2 rounded-xl p-5 border border-[#e0e0e0] dark:border-[#473c53] bg-white dark:bg-[#231b2e] shadow-sm">
                <div className="flex justify-between items-start">
                  <p className="text-[#637588] dark:text-[#ab9db8] text-sm font-medium uppercase tracking-wider">
                    Enviados
                  </p>
                  <span className="material-symbols-outlined text-emerald-500">check_circle</span>
                </div>
                <p className="text-[#111418] dark:text-white text-3xl font-bold leading-tight">{metricas.enviados}</p>
                <div className="text-sm text-[#637588] dark:text-[#ab9db8]">
                  {porcentaje(metricas.enviados, metricas.total)}% confirmado como enviado
                </div>
              </div>

              <div className="flex flex-col gap-2 rounded-xl p-5 border border-[#e0e0e0] dark:border-[#473c53] bg-white dark:bg-[#231b2e] shadow-sm">
                <div className="flex justify-between items-start">
                  <p className="text-[#637588] dark:text-[#ab9db8] text-sm font-medium uppercase tracking-wider">
                    Fallidos
                  </p>
                  <span className="material-symbols-outlined text-red-500">error</span>
                </div>
                <p className="text-[#111418] dark:text-white text-3xl font-bold leading-tight">{metricas.fallidos}</p>
                <div className="text-sm text-[#637588] dark:text-[#ab9db8]">
                  {porcentaje(metricas.fallidos, metricas.total)}% requiere revisión
                </div>
              </div>
            </div>

            <div className="rounded-xl p-5 border border-[#e0e0e0] dark:border-[#473c53] bg-white dark:bg-[#231b2e] shadow-sm">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-4">
                <div>
                  <h3 className="text-lg font-bold text-[#111418] dark:text-white">Rendimiento de envíos</h3>
                  <p className="text-sm text-[#637588] dark:text-[#ab9db8]">
                    Lectura funcional del proceso: aceptados, enviados, fallidos y otros estados registrados.
                  </p>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  <div>
                    <p className="text-[#637588] dark:text-[#ab9db8]">Sin fallo</p>
                    <p className="font-bold text-primary">{metricas.tasaSinFallo}%</p>
                  </div>
                  <div>
                    <p className="text-[#637588] dark:text-[#ab9db8]">Aceptados</p>
                    <p className="font-bold text-sky-500">{metricas.aceptados}</p>
                  </div>
                  <div>
                    <p className="text-[#637588] dark:text-[#ab9db8]">Enviados</p>
                    <p className="font-bold text-emerald-500">{metricas.enviados}</p>
                  </div>
                  <div>
                    <p className="text-[#637588] dark:text-[#ab9db8]">Otros</p>
                    <p className="font-bold text-amber-500">{metricas.pendientes}</p>
                  </div>
                </div>
              </div>
              <div className="w-full bg-[#e0e0e0] dark:bg-[#362f40] h-3 rounded-full overflow-hidden flex">
                <div className="bg-sky-500 h-3" style={{ width: `${porcentaje(metricas.aceptados, metricas.total)}%` }} />
                <div className="bg-emerald-500 h-3" style={{ width: `${porcentaje(metricas.enviados, metricas.total)}%` }} />
                <div className="bg-red-500 h-3" style={{ width: `${porcentaje(metricas.fallidos, metricas.total)}%` }} />
                <div className="bg-amber-500 h-3" style={{ width: `${porcentaje(metricas.pendientes, metricas.total)}%` }} />
              </div>
              <div className="mt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-sm text-[#637588] dark:text-[#ab9db8]">
                <div className="flex flex-wrap gap-x-4 gap-y-2">
                  <span className="inline-flex items-center gap-2">
                    <span className="size-2 rounded-full bg-sky-500" />
                    Aceptado
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <span className="size-2 rounded-full bg-emerald-500" />
                    Enviado
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <span className="size-2 rounded-full bg-red-500" />
                    Fallido
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <span className="size-2 rounded-full bg-amber-500" />
                    Otros estados
                  </span>
                </div>
                <span>
                  Último registro: {metricas.ultimo ? formatoFechaHora(metricas.ultimo.enviadoEn) : "—"}
                </span>
                <span>
                  {eventoFecha ? `Evento: ${formatoFechaHora(eventoFecha)}` : `Evento #${idEvento}`}
                </span>
              </div>
            </div>

            <div className="flex flex-col md:flex-row justify-between items-center gap-4 bg-white dark:bg-[#231b2e] p-2 rounded-xl border border-[#e0e0e0] dark:border-[#473c53]">
              <div className="relative w-full md:min-w-[320px]">
                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-[#637588] dark:text-[#ab9db8]">
                  search
                </span>
                <input
                  className="w-full bg-[#f7f6f8] dark:bg-[#191121] text-[#111418] dark:text-white pl-10 pr-4 py-2 rounded-lg border-none focus:ring-2 focus:ring-primary placeholder-[#637588] dark:placeholder-[#645970] text-sm"
                  placeholder="Buscar invitado, teléfono, canal o error..."
                  type="text"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
              </div>

              <select
                className="w-full md:w-auto bg-[#f7f6f8] dark:bg-[#191121] text-[#111418] dark:text-white px-3 py-2 rounded-lg border-none focus:ring-2 focus:ring-primary text-sm"
                value={estado}
                onChange={(e) => setEstado(e.target.value)}
              >
                <option value="TODOS">Todos los estados</option>
                {estados.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>

            <div className="pb-10 rounded-xl border border-[#e0e0e0] dark:border-[#473c53] bg-white dark:bg-[#231b2e] shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-[#e0e0e0] dark:border-[#362f40]">
                <h3 className="text-lg font-bold text-[#111418] dark:text-white">Logs de invitaciones</h3>
                <p className="text-sm text-[#637588] dark:text-[#ab9db8]">
                  Mostrando {filtrados.length} de {logs.length} registros
                </p>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-[#f7f6f8] dark:bg-[#191121] text-[#637588] dark:text-[#ab9db8]">
                    <tr>
                      <th className="text-left font-semibold uppercase tracking-wider px-5 py-3">ID</th>
                      <th className="text-left font-semibold uppercase tracking-wider px-5 py-3">Invitado</th>
                      <th className="text-left font-semibold uppercase tracking-wider px-5 py-3">Canal</th>
                      <th className="text-left font-semibold uppercase tracking-wider px-5 py-3">Estado</th>
                      <th className="text-left font-semibold uppercase tracking-wider px-5 py-3">Enviado en</th>
                      <th className="text-left font-semibold uppercase tracking-wider px-5 py-3">Detalle</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#e0e0e0] dark:divide-[#362f40]">
                    {filtrados.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-5 py-10 text-center text-[#637588] dark:text-[#ab9db8]">
                          No se encontraron logs.
                        </td>
                      </tr>
                    ) : (
                      filtrados.map((log) => {
                        const invitadoNombre = `${log.invitado?.nombre ?? ""} ${log.invitado?.apellido ?? ""}`.trim() || "—";
                        return (
                          <tr key={log.id} className="hover:bg-black/5 dark:hover:bg-white/5 align-top">
                            <td className="px-5 py-4 text-[#111418] dark:text-white font-semibold">
                              #{String(log.id).padStart(3, "0")}
                            </td>
                            <td className="px-5 py-4 text-[#111418] dark:text-white">
                              {invitadoNombre}
                              <div className="text-xs text-[#637588] dark:text-[#ab9db8]">
                                {log.invitado?.telefono || "Sin teléfono"}
                              </div>
                            </td>
                            <td className="px-5 py-4 text-[#111418] dark:text-white">
                              <div className="font-semibold">{log.canal}</div>
                              <div className="text-xs text-[#637588] dark:text-[#ab9db8]">{log.modo}</div>
                            </td>
                            <td className="px-5 py-4">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${claseEstado(log.estado)}`}>
                                {log.estado}
                              </span>
                            </td>
                            <td className="px-5 py-4 text-[#111418] dark:text-white whitespace-nowrap">
                              {formatoFechaHora(log.enviadoEn)}
                            </td>
                            <td className="px-5 py-4 text-[#111418] dark:text-white min-w-[180px]">
                              <button
                                type="button"
                                onClick={() => setDetailLog(log)}
                                className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold text-white bg-primary hover:bg-primary/90 transition-colors"
                              >
                                <span className="material-symbols-outlined text-[16px]">visibility</span>
                                Ver Detalles
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
        </div>
      </div>

      {detailLog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button
            className="absolute inset-0 bg-black/60 backdrop-blur-[2px]"
            onClick={() => setDetailLog(null)}
            aria-label="Cerrar"
            type="button"
          />

          <div className="relative w-full max-w-3xl max-h-[90vh] rounded-2xl border border-[#e0e0e0] dark:border-[#362f40] bg-white dark:bg-[#231b2e] shadow-2xl overflow-hidden flex flex-col">
            <div className="p-6 border-b border-[#e0e0e0] dark:border-[#362f40] flex items-start gap-4">
              <div className="size-11 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                <span className="material-symbols-outlined text-primary">article</span>
              </div>

              <div className="min-w-0">
                <h3 className="text-xl md:text-2xl font-serif font-semibold text-[#111418] dark:text-white">
                  Detalle del log
                </h3>
                <p className="text-sm text-[#637588] dark:text-[#ab9db8] mt-1">
                  #{String(detailLog.id).padStart(3, "0")} · {detailLog.estado} · {formatoFechaHora(detailLog.enviadoEn)}
                </p>
              </div>

              <button
                type="button"
                onClick={() => setDetailLog(null)}
                className="ml-auto text-[#637588] dark:text-[#ab9db8] hover:text-primary transition-colors"
                aria-label="Cerrar"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="p-6 overflow-y-auto min-h-0 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div className="rounded-xl border border-[#e0e0e0] dark:border-[#473c53] bg-[#f7f6f8] dark:bg-[#191121] p-4">
                  <p className="text-[#637588] dark:text-[#ab9db8]">Invitado</p>
                  <p className="font-semibold text-[#111418] dark:text-white mt-1">
                    {`${detailLog.invitado?.nombre ?? ""} ${detailLog.invitado?.apellido ?? ""}`.trim() || "—"}
                  </p>
                </div>
                <div className="rounded-xl border border-[#e0e0e0] dark:border-[#473c53] bg-[#f7f6f8] dark:bg-[#191121] p-4">
                  <p className="text-[#637588] dark:text-[#ab9db8]">Canal y modo</p>
                  <p className="font-semibold text-[#111418] dark:text-white mt-1">
                    {detailLog.canal} · {detailLog.modo}
                  </p>
                </div>
              </div>

              <div className="rounded-xl border border-[#e0e0e0] dark:border-[#473c53] bg-[#f7f6f8] dark:bg-[#191121] p-4">
                <p className="text-sm font-semibold text-[#637588] dark:text-[#ab9db8] mb-3">
                  {detailLog.errorDetalle ? "Error" : "Mensaje"}
                </p>
                <pre className="whitespace-pre-wrap break-words font-sans text-sm leading-relaxed text-[#111418] dark:text-white">
                  {detailLog.errorDetalle || detailLog.mensaje || "Sin detalle registrado."}
                </pre>
              </div>
            </div>

            <div className="p-6 border-t border-[#e0e0e0] dark:border-[#362f40] flex justify-end">
              <button
                type="button"
                onClick={() => setDetailLog(null)}
                className="px-5 py-2.5 rounded-lg text-sm font-semibold text-white bg-primary hover:bg-primary/90 transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default InvitationLogsPage;
